import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { PageHeader, Stars, StatusBadge, Empty } from "../components/Shared";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Skeleton } from "../components/ui/skeleton";
import { Badge } from "../components/ui/badge";
import { Search, MapPin, BadgeCheck, Bookmark, Send } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const CATS = ["IT Services", "Logistics", "Marketing", "Legal", "HR", "Finance", "Manufacturing"];
const SIZES = ["1-50", "51-200", "201-1000", "1000+"];
const COMPLIANCE = ["Verified", "Pending", "Unverified"];

export default function Discover() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [vendors, setVendors] = useState(null);
  const [q, setQ] = useState("");
  const [cats, setCats] = useState([]);
  const [location, setLocation] = useState("");
  const [comp, setComp] = useState("");
  const [minRating, setMinRating] = useState("");
  const [size, setSize] = useState("");
  const [sort, setSort] = useState("relevance");

  const fetchVendors = async () => {
    setVendors(null);
    try {
      const { data } = await api.get("/vendors", {
        params: {
          q: q || undefined,
          categories: cats.length ? cats.join(",") : undefined,
          location: location || undefined,
          compliance: comp || undefined,
          min_rating: minRating || undefined,
          company_size: size || undefined,
          sort,
        },
      });
      setVendors(data);
    } catch {
      setVendors([]);
    }
  };

  useEffect(() => {
    const t = setTimeout(fetchVendors, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, cats, location, comp, minRating, size, sort]);

  const toggleCat = (c) => setCats((p) => (p.includes(c) ? p.filter((x) => x !== c) : [...p, c]));

  const toggleFav = async (vid) => {
    try {
      await api.post(`/vendors/${vid}/favorite`);
      await refreshUser();
      toast.success("Updated favourites");
    } catch (e) { toast.error("Could not update"); }
  };

  const favs = user?.favorites || [];

  return (
    <div className="animate-fade-in">
      <PageHeader title="Discover Vendors" subtitle="Search 1,200+ verified suppliers across India" />

      <div className="grid grid-cols-1 lg:grid-cols-[260px,1fr] gap-6">
        {/* Filters */}
        <aside className="bg-white border border-zinc-200 rounded-md p-5 h-fit sticky top-20" data-testid="discover-filters">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-sm">Filters</h3>
            <button onClick={() => { setCats([]); setLocation(""); setComp(""); setMinRating(""); setSize(""); }} className="text-xs text-[#6366F1] hover:underline" data-testid="clear-filters">Clear</button>
          </div>

          <div className="space-y-5">
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">Category</Label>
              <div className="space-y-2">
                {CATS.map((c) => (
                  <label key={c} className="flex items-center gap-2 cursor-pointer text-sm">
                    <Checkbox checked={cats.includes(c)} onCheckedChange={() => toggleCat(c)} data-testid={`filter-cat-${c.toLowerCase().replace(/\s+/g, "-")}`} />
                    <span>{c}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">Location</Label>
              <Input placeholder="City or state" value={location} onChange={(e) => setLocation(e.target.value)} className="h-9" data-testid="filter-location" />
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">Compliance</Label>
              <Select value={comp || "all"} onValueChange={(val) => setComp(val === "all" ? "" : val)}>
                <SelectTrigger className="h-9" data-testid="filter-compliance"><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any</SelectItem>
                  {COMPLIANCE.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">Min rating</Label>
              <div className="flex gap-1">
                {[1,2,3,4,5].map((r) => (
                  <button key={r} onClick={() => setMinRating(minRating === String(r) ? "" : String(r))} className={`h-9 flex-1 border rounded-md text-xs font-medium transition-colors ${minRating === String(r) ? "bg-[#6366F1] text-white border-[#6366F1]" : "border-zinc-200 hover:border-[#6366F1]"}`} data-testid={`filter-rating-${r}`}>
                    {r}★
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">Company size</Label>
              <Select value={size || "all"} onValueChange={(val) => setSize(val === "all" ? "" : val)}>
                <SelectTrigger className="h-9" data-testid="filter-size"><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any</SelectItem>
                  {SIZES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </aside>

        {/* Results */}
        <div>
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <Input placeholder="Search by vendor name, category, or keyword..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 h-10" data-testid="discover-search" />
            </div>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="w-44 h-10" data-testid="sort-by"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="relevance">Relevance</SelectItem>
                <SelectItem value="rating">Rating</SelectItem>
                <SelectItem value="recent">Recently added</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {vendors === null && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[1,2,3,4].map(i => <Skeleton key={i} className="h-44" />)}
            </div>
          )}

          {vendors && vendors.length === 0 && (
            <Empty title="No vendors match" subtitle="Try adjusting your filters or search query." />
          )}

          {vendors && vendors.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="vendor-grid">
              {vendors.map((v) => (
                <div key={v.id} className="bg-white border border-zinc-200 rounded-md p-5 hover:border-[#6366F1] transition-colors group" data-testid="vendor-card">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-md bg-zinc-100 border border-zinc-200 grid place-items-center font-bold text-zinc-600 overflow-hidden flex-shrink-0">
                      {v.logo ? <img src={v.logo} alt={v.name} className="w-full h-full object-cover" /> : v.name[0]}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5">
                            <h3 className="font-semibold text-base truncate">{v.name}</h3>
                            {v.verified && <BadgeCheck className="w-4 h-4 text-emerald-500 flex-shrink-0" />}
                          </div>
                          <div className="text-xs text-zinc-500 mt-0.5 flex items-center gap-1"><MapPin className="w-3 h-3" /> {v.location}</div>
                        </div>
                        <button onClick={() => toggleFav(v.id)} className="p-1 hover:bg-zinc-100 rounded" data-testid="favorite-btn">
                          <Bookmark className={`w-4 h-4 ${favs.includes(v.id) ? "fill-[#6366F1] text-[#6366F1]" : "text-zinc-400"}`} />
                        </button>
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        <Badge variant="secondary" className="text-xs">{v.category}</Badge>
                        <Stars value={v.rating} />
                        <span className="text-xs text-zinc-500">{v.rating}</span>
                      </div>
                      <p className="text-xs text-zinc-600 mt-2 line-clamp-2">{v.tagline}</p>
                      <div className="flex gap-2 mt-3">
                        <Button size="sm" variant="outline" onClick={() => navigate(`/vendor/${v.id}`)} className="flex-1" data-testid="view-profile-btn">View Profile</Button>
                        <Button size="sm" className="flex-1 bg-[#6366F1] hover:bg-[#4F46E5]" onClick={() => navigate(`/rfq?vendor=${v.id}`)} data-testid="add-rfq-btn"><Send className="w-3 h-3 mr-1" /> Add to RFQ</Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
